import React from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, ArrowRight, Shield } from 'lucide-react';
import type { WhatIfResponse, AllBasicPredictions } from '../../types/predictions';
import { getStatusColor, getBasicName } from '../../types/predictions';

interface ImpactPreviewProps {
  baseline: AllBasicPredictions;
  impactResult: WhatIfResponse;
}

/**
 * ImpactPreview Component
 *
 * Displays before/after comparison of BASIC scores with hypothetical violations
 *
 * Features:
 * - Side-by-side comparison of baseline vs hypothetical
 * - Highlights changes in measure and percentile
 * - Shows delta with visual indicators
 * - Threshold breach warnings
 * - Impact severity badges
 * - Color-coded status changes
 */
export const ImpactPreview: React.FC<ImpactPreviewProps> = ({
  baseline,
  impactResult,
}) => {
  // Get severity badge color
  const getSeverityColor = (severity: string) => {
    switch (severity.toUpperCase()) {
      case 'MINIMAL':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'MODERATE':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'SIGNIFICANT':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'SEVERE':
      case 'CRITICAL':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Check if there are threshold breaches
  const hasThresholdBreaches = impactResult.impacts.some(
    impact => impact.currentPrediction.percentile < impact.currentPrediction.threshold &&
              impact.newPrediction.percentile >= impact.currentPrediction.threshold
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Impact Analysis</h3>
        </div>
        <p className="text-sm text-gray-600">
          Predicted changes to BASIC scores with {impactResult.impacts.length} hypothetical {impactResult.impacts.length === 1 ? 'violation' : 'violations'}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Affected BASICs</p>
          <p className="text-2xl font-bold text-gray-900">{impactResult.summary.totalImpacts}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status Changes</p>
          <p className="text-2xl font-bold text-orange-600">{impactResult.summary.statusChanges}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Avg % Increase</p>
          <p className="text-2xl font-bold text-red-600">
            +{impactResult.summary.averagePercentileIncrease.toFixed(0)}
          </p>
        </div>
      </div>

      {/* Threshold Breach Warning */}
      {hasThresholdBreaches && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-red-900 mb-1">Threshold Breach Alert</h4>
              <p className="text-sm text-red-800">
                One or more BASICs would breach their threshold with these violations. This could
                trigger interventions from FMCSA.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Impact Cards */}
      <div className="space-y-4">
        {impactResult.impacts.map((impact) => {
          const baselineColors = getStatusColor(impact.currentPrediction.status);
          const newColors = getStatusColor(impact.newPrediction.status);
          const basicName = getBasicName(impact.basicId as any);

          const willBreachThreshold =
            impact.currentPrediction.percentile < impact.currentPrediction.threshold &&
            impact.newPrediction.percentile >= impact.currentPrediction.threshold;

          return (
            <div
              key={impact.basicId}
              className={`bg-white rounded-lg shadow-md p-5 border-2 ${
                willBreachThreshold ? 'border-red-400' : 'border-gray-200'
              }`}
            >
              {/* BASIC Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gray-600" />
                  <div>
                    <h4 className="font-bold text-gray-900">{impact.basicId}</h4>
                    <p className="text-xs text-gray-500">{basicName}</p>
                  </div>
                </div>

                {/* Impact Severity Badge */}
                <span
                  className={`px-3 py-1 text-xs font-bold rounded-full border ${getSeverityColor(
                    impact.change.riskIncrease
                  )}`}
                >
                  {impact.change.riskIncrease} Impact
                </span>
              </div>

              {/* Before vs After Comparison */}
              <div className="grid grid-cols-[1fr,auto,1fr] gap-4 mb-4">
                {/* Current (Before) */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-600 uppercase tracking-wide mb-2">Current</p>
                  <div className="space-y-1">
                    <div>
                      <p className="text-3xl font-bold text-gray-900">
                        {impact.currentPrediction.percentile}
                        <span className="text-sm text-gray-500">th</span>
                      </p>
                    </div>
                    <div
                      className="inline-block px-2 py-1 rounded text-xs font-semibold"
                      style={{
                        backgroundColor: baselineColors.bg,
                        color: baselineColors.text,
                      }}
                    >
                      {impact.currentPrediction.status}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Measure: {impact.currentPrediction.measure !== null && impact.currentPrediction.measure !== undefined
                        ? impact.currentPrediction.measure.toFixed(4)
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center">
                  <ArrowRight className="w-6 h-6 text-gray-400" />
                </div>

                {/* With Violations (After) */}
                <div className={`rounded-lg p-4 ${
                  willBreachThreshold ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'
                }`}>
                  <p className="text-xs text-gray-600 uppercase tracking-wide mb-2">
                    With Violations
                  </p>
                  <div className="space-y-1">
                    <div className="flex items-baseline gap-2">
                      <p className="text-3xl font-bold text-orange-700">
                        {impact.newPrediction.percentile}
                        <span className="text-sm">th</span>
                      </p>
                      {impact.change.percentileDelta > 0 && (
                        <TrendingUp className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div
                      className="inline-block px-2 py-1 rounded text-xs font-semibold"
                      style={{
                        backgroundColor: newColors.bg,
                        color: newColors.text,
                      }}
                    >
                      {impact.newPrediction.status}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Measure: {impact.newPrediction.measure !== null && impact.newPrediction.measure !== undefined
                        ? impact.newPrediction.measure.toFixed(4)
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Change Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Impact Details
                </h5>

                <div className="grid grid-cols-2 gap-3">
                  {/* Percentile Change */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Percentile Change:</span>
                    <span
                      className={`text-sm font-bold ${
                        impact.change.percentileDelta > 0 ? 'text-red-600' : 'text-gray-600'
                      }`}
                    >
                      {impact.change.percentileDelta > 0 ? '+' : ''}
                      {impact.change.percentileDelta} points
                    </span>
                  </div>

                  {/* Measure Change */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Measure Change:</span>
                    <span
                      className={`text-sm font-bold ${
                        impact.change.measureDelta > 0 ? 'text-red-600' : 'text-gray-600'
                      }`}
                    >
                      {impact.change.measureDelta > 0 ? '+' : ''}
                      {impact.change.measureDelta.toFixed(4)}
                    </span>
                  </div>

                  {/* Status Change */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Status Change:</span>
                    <span className="text-sm font-bold">
                      {impact.change.statusChange ? (
                        <span className="text-orange-600">Yes</span>
                      ) : (
                        <span className="text-gray-500">No</span>
                      )}
                    </span>
                  </div>

                  {/* Threshold Status */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Threshold:</span>
                    <span className="text-sm font-bold">
                      {willBreachThreshold ? (
                        <span className="text-red-600">Would Breach</span>
                      ) : impact.newPrediction.percentile < impact.newPrediction.threshold ? (
                        <span className="text-green-600">Safe</span>
                      ) : (
                        <span className="text-orange-600">Above Threshold</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Threshold Breach Warning for This BASIC */}
              {willBreachThreshold && (
                <div className="mt-3 bg-red-100 border border-red-300 text-red-800 p-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Warning:</span> This BASIC would breach the{' '}
                    {impact.currentPrediction.threshold}th percentile threshold, moving from{' '}
                    {impact.currentPrediction.percentile}th to {impact.newPrediction.percentile}th
                    percentile.
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
        <p className="font-semibold mb-1">Note:</p>
        <p>
          These are predictions based on exact FMCSA SMS methodology. Actual scores may
          vary slightly. Use this analysis for planning and prevention strategies.
        </p>
      </div>
    </div>
  );
};

export default ImpactPreview;
